import { inject, reactive, markRaw } from "vue";
import Keycloak from "keycloak-js";
import { cleanAuthData, clearOrganizationCache, clearOrgRecoveryFlag, hasOrganizationClaim, markLogoutAndCleanup, RECOVERY_FLAG, saveLoginEmail, } from "@/shared/utils/authCleanup";
import { logger } from "@/infrastructure/services/LoggerService";
import { routes } from "@/router/index";
import { initializeSession, checkSessionValidity, clearSession, } from "@/shared/utils/sessionManager";
import api from "@/plugins/api";
import { createAuthBootstrapService, } from "@/services/auth-bootstrap";
import { loadByoHost } from "@/shared/utils/byoHost";
let keycloakConfig;
if (import.meta.env.PROD) {
    keycloakConfig = import.meta.env.VITE_KEYCLOAK_CONFIG_FILE;
}
else {
    keycloakConfig = {
        url: import.meta.env.VITE_KEYCLOAK_AUTH_SERVER_URL,
        realm: import.meta.env.VITE_KEYCLOAK_REALM,
        clientId: import.meta.env.VITE_KEYCLOAK_RESOURCE,
    };
}
export const KEYCLOAK_KEY = Symbol("KEYCLOAK_KEY");
// `let`: configureKeycloakForHost() replaces the instance; importers follow the live binding.
let keycloak = new Keycloak(keycloakConfig);
let byoHost = null;
const authState = reactive({
    isAuthenticated: false,
    isInitialized: false,
    keycloakInstance: markRaw(keycloak),
    login: async (options) => {
        logger.debug("Starting login process...");
        logger.debug("Current auth state:", {
            isInitialized: authState.isInitialized,
            isAuthenticated: authState.isAuthenticated,
            hasKeycloakLogin: !!keycloak.login,
            keycloakAuthenticated: keycloak.authenticated,
        });
        try {
            // If on session-ended page or keycloak is not properly initialized, we need to initialize it first
            const isOnSessionEndedPage = window.location.pathname === "/session-ended" ||
                window.location.pathname.includes("/session-ended");
            // Check if keycloak needs initialization
            const needsInit = !keycloak.authenticated &&
                (isOnSessionEndedPage || !authState.isInitialized || !keycloak.login);
            if (needsInit) {
                logger.debug("Keycloak needs initialization before login. Initializing now...");
                // Reset the state to allow reinitialization
                authState.isInitialized = false;
                await initializeKeycloak();
                logger.debug("Keycloak initialization completed. Ready for login.");
            }
            if (!authState.isInitialized) {
                logger.debug("Waiting for Keycloak initialization to complete...");
                let attempts = 0;
                const maxAttempts = 150;
                while (!authState.isInitialized && attempts < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    attempts++;
                }
                if (!authState.isInitialized) {
                    throw new Error("Keycloak initialization timeout - failed to initialize within 15 seconds");
                }
                logger.debug("Keycloak initialization completed, proceeding with login");
            }
            if (!keycloak.login) {
                throw new Error("Keycloak login method not available after initialization");
            }
            const userLocale = getUserLocale();
            // Determine organization to use: prioritize explicit parameter over stored value
            // This allows organization context from URL (e.g., /confirm/ClientA) to take precedence
            const organizationToUse = byoHost?.organization || options?.organization || localStorage.getItem("organizationCache");
            // Build Keycloak login options
            const keycloakLoginOptions = {
                scope: organizationToUse ? `organization:${organizationToUse}` : getScope(),
                redirectUri: options?.redirectUri,
                locale: userLocale,
            };
            // Add organization as kc_idp_hint to skip organization selection
            // This tells Keycloak which organization to use directly
            if (organizationToUse) {
                keycloakLoginOptions.idpHint = organizationToUse;
                logger.debug("Using kc_idp_hint (organization):", organizationToUse);
                if (options?.organization) {
                    logger.debug("Organization from explicit parameter (redirect context)");
                }
                else {
                    logger.debug("Organization from localStorage cache");
                }
            }
            // OP-5119: when we are recovering from an org-less session, force a
            // fresh authentication so Keycloak's SSO short-circuit (which keeps
            // re-using the deleted-org session note) cannot bypass the
            // OrganizationAuthenticator.action() flow.
            if (sessionStorage.getItem(RECOVERY_FLAG)) {
                keycloakLoginOptions.prompt = "login";
                logger.debug("OP-5119 recovery: forcing prompt=login");
            }
            // Add loginHint if provided (Keycloak official parameter to pre-fill email)
            if (options?.loginHint) {
                keycloakLoginOptions.loginHint = options.loginHint;
                logger.debug("Using login_hint (email):", options.loginHint);
            }
            logger.debug("Calling keycloak.login() with scope:", keycloakLoginOptions.scope);
            logger.debug("Redirect URI:", options?.redirectUri);
            logger.debug("Locale for Keycloak:", userLocale);
            logger.debug("Login hint:", options?.loginHint || "none");
            logger.debug("IDP hint (organization):", organizationToUse || "none");
            await keycloak.login(keycloakLoginOptions);
            logger.debug("keycloak.login() call completed");
        }
        catch (error) {
            logger.debug("Login failed:", error);
            throw error;
        }
    },
    logout: async () => {
        logger.debug("Starting logout process...");
        markLogoutAndCleanup();
        clearSession();
        authState.isAuthenticated = false;
        if (keycloak && keycloak.authenticated) {
            try {
                await keycloak.logout({
                    redirectUri: window.location.origin + "/session-ended",
                });
                return;
            }
            catch (logoutError) {
                logger.debug("Keycloak server logout failed:", logoutError);
            }
        }
        window.location.replace(window.location.origin + "/session-ended");
    },
    reinitialize: async () => {
        logger.debug("Reinitializing Keycloak...");
        sessionStorage.removeItem("force-logout");
        authState.isAuthenticated = false;
        authState.isInitialized = false;
        await initializeKeycloak();
    },
});
function getUserLocale() {
    const selectedLocale = localStorage.getItem("selectedLocale") || localStorage.getItem("locale");
    if (selectedLocale) {
        if (selectedLocale.startsWith("fr"))
            return "fr";
        if (selectedLocale.startsWith("nl"))
            return "nl";
        if (selectedLocale.startsWith("en"))
            return "en";
    }
    return "en";
}
// The organization alias (what kc_idp_hint expects, not the UUID): the claim is
// { "org-name": { "id": "uuid", ... } } or a plain string.
function tokenOrganization() {
    const organizationObj = keycloak.idTokenParsed?.organization;
    if (typeof organizationObj === "string") {
        return organizationObj || undefined;
    }
    if (typeof organizationObj === "object" && organizationObj !== null) {
        return Object.keys(organizationObj)[0];
    }
    return undefined;
}
// Helper function to save organization from token
function saveOrganizationFromToken() {
    const organization = tokenOrganization();
    if (!organization) {
        logger.debug("No organization found in ID token");
        return;
    }
    localStorage.setItem("organizationCache", organization);
    logger.debug("Organization name saved to cache:", organization);
}
// Helper function to get scope with organization
function getScope() {
    // Read from localStorage to persist across Keycloak redirects
    const storedOrganization = byoHost?.organization ?? localStorage.getItem("organizationCache");
    return storedOrganization ? `organization:${storedOrganization}` : "organization";
}
// Drop locally cached tokens (sessionStorage + the keycloak instance's
// in-memory copies) without touching the recovery flag or the org cache.
// Mirrors opay-front's helper so the two apps stay aligned.
function clearInvalidTokens() {
    logger.debug("Clearing any invalid or expired tokens from sessionStorage");
    cleanAuthData();
    keycloak.token = undefined;
    keycloak.refreshToken = undefined;
}
// Discard the org-less authenticated session produced by case (b) so the
// router guard triggers a forced login with the plain `organization` scope.
function discardOrgLessSession() {
    logger.debug("Silent SSO succeeded without an organization claim; discarding session (OP-5119)");
    clearOrganizationCache();
    clearInvalidTokens();
    authState.isAuthenticated = false;
    authState.isInitialized = true;
}
// Ends Keycloak's SSO session, so the terminal page is sticky on refresh.
function signOutToTerminalPage(reason) {
    markLogoutAndCleanup();
    authState.isAuthenticated = false;
    authState.isInitialized = true;
    const terminalUrl = `${window.location.origin}/session-ended?reason=${reason}`;
    if (keycloak && keycloak.authenticated) {
        keycloak.logout({ redirectUri: terminalUrl }).catch((logoutError) => {
            logger.debug("Keycloak logout during OP-5119 escalation failed:", logoutError);
            window.location.replace(terminalUrl);
        });
        return;
    }
    window.location.replace(terminalUrl);
}
// State-B action: a `prompt=login` retry STILL produced an org-less token
// (typically the 0-orgs case).
function escalateOrgLessSession() {
    logger.debug("Recovery retry still produced an org-less token; escalating to logout (OP-5119)");
    signOutToTerminalPage("no-org");
}
// Dispatcher for an authenticated-but-org-less session (OP-5119 case b).
// First detection → State A (set flag, soft discard, router will retry with
// prompt=login). Second detection → State B (escalate via logout).
function handleOrgLessSession() {
    if (sessionStorage.getItem(RECOVERY_FLAG)) {
        escalateOrgLessSession();
        return;
    }
    sessionStorage.setItem(RECOVERY_FLAG, "1");
    discardOrgLessSession();
}
function isPublicPage(pathname) {
    const matchesRoute = (routePath, pathname) => {
        const pattern = routePath.replace(/:\w+/g, "[^/]+");
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(pathname);
    };
    for (const route of routes) {
        if (route.path.includes("*") || route.path.includes("(.*)")) {
            continue;
        }
        if (route.meta?.isPublicPage) {
            if (matchesRoute(route.path, pathname)) {
                return true;
            }
        }
    }
    return false;
}
function shouldSkipAutoAuth() {
    const forceLogout = sessionStorage.getItem("force-logout");
    if (!forceLogout)
        return false;
    const logoutTime = parseInt(forceLogout);
    const currentTime = Date.now();
    const timeDiff = currentTime - logoutTime;
    if (timeDiff < 10 * 60 * 1000 && !window.location.pathname.includes("/session-ended")) {
        return true;
    }
    if (window.location.pathname.includes("/session-ended")) {
        sessionStorage.removeItem("force-logout");
    }
    if (timeDiff >= 10 * 60 * 1000) {
        sessionStorage.removeItem("force-logout");
    }
    return false;
}
// Module-level singleton for the auth-bootstrap service. Lazily constructed
// on first access so the keycloak instance and api axios singleton are both
// initialized before the service captures them via closure. Exported for
// downstream consumers (e.g. the 403 recovery interceptor in api.ts).
let authBootstrapInstance = null;
export function getAuthBootstrap() {
    if (authBootstrapInstance === null) {
        authBootstrapInstance = createAuthBootstrapService({
            postBootstrap: async (init) => {
                // BFF response interceptor unwraps ApiResponse<T> for unwrapEnvelope: true,
                // so `response.data` is the BootstrapAuthResponseV1 directly. We keep
                // the unwrap fallback for defensive double-shape handling.
                const response = await api.post("/auth/bootstrap", undefined, {
                    unwrapEnvelope: true,
                    signal: init?.signal,
                });
                const body = response.data;
                if (body !== null &&
                    typeof body === "object" &&
                    "data" in body &&
                    body.data !== null &&
                    typeof body.data === "object" &&
                    "rightsChanged" in body.data) {
                    return body.data;
                }
                return body;
            },
            keycloak: () => keycloak,
            logger,
        });
    }
    return authBootstrapInstance;
}
async function initializeKeycloak() {
    try {
        const skipAutoAuth = shouldSkipAutoAuth();
        logger.debug("Starting Keycloak initialization...");
        if (!skipAutoAuth) {
            cleanAuthData();
            keycloak.token = undefined;
            keycloak.refreshToken = undefined;
        }
        const scope = getScope();
        const initOptions = {
            onLoad: "check-sso",
            scope: scope,
            silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
            // A customer hostname makes Keycloak third-party: true lets keycloak-js drop the silent
            // iframe where the browser blocks its cookies, instead of reporting the user signed out.
            silentCheckSsoFallback: byoHost !== null,
        };
        const initPromise = keycloak.init(initOptions).catch((initError) => {
            logger.debug("Keycloak init promise rejected:", initError);
            throw initError;
        });
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Keycloak initialization timeout")), 15000);
        });
        const authenticated = (await Promise.race([initPromise, timeoutPromise]));
        logger.debug("Keycloak initialization completed. Authenticated:", authenticated);
        // A customer hostname serves its own organization only; Keycloak's organization scope is a hint.
        // Checked before the org-less case: a token without the claim is not this hostname's
        // organization either, and a prompt=login retry cannot change that.
        if (authenticated && byoHost && tokenOrganization() !== byoHost.organization) {
            logger.debug("Token organization does not match this hostname's organization");
            signOutToTerminalPage("wrong-org");
            return;
        }
        // Case (b) (OP-5119): silent SSO succeeded but the id_token has no
        // `organization` claim. The cache being set or empty does not matter —
        // an org-less token is functionally invalid for this app. Hand off to
        // the state machine: first hit retries with prompt=login, second hit
        // escalates to a logout + terminal page.
        if (authenticated && !hasOrganizationClaim(keycloak.idTokenParsed)) {
            handleOrgLessSession();
            return;
        }
        authState.isAuthenticated = authenticated;
        authState.isInitialized = true;
        if (authenticated) {
            if (keycloak.token && keycloak.refreshToken) {
                sessionStorage.setItem("kc-token", keycloak.token);
                sessionStorage.setItem("kc-refresh-token", keycloak.refreshToken);
                if (keycloak.tokenParsed?.email) {
                    saveLoginEmail(keycloak.tokenParsed.email);
                }
                // Save organization from token for future logins
                saveOrganizationFromToken();
                clearOrgRecoveryFlag(); // OP-5119: circuit reset on success
                initializeSession();
                // OP-1406: fire-and-forget auth bootstrap so the user's default roles
                // are materialized in Keycloak before downstream API calls. Runs only
                // on the happy path (authenticated + valid organization claim — the
                // OP-5119 org-less recovery branches return earlier above).
                getAuthBootstrap().start();
            }
            setInterval(async () => {
                try {
                    if (!keycloak.token || !keycloak.refreshToken) {
                        authState.isAuthenticated = false;
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                        clearSession();
                        return;
                    }
                    const sessionState = checkSessionValidity();
                    if (!sessionState.isValid) {
                        authState.isAuthenticated = false;
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                        clearSession();
                        keycloak.logout();
                        if (window.location.pathname !== "/session-ended" &&
                            !window.location.pathname.startsWith("/error")) {
                            window.location.replace("/session-ended");
                        }
                        return;
                    }
                    const tokenUpdated = await keycloak.updateToken(30);
                    if (tokenUpdated) {
                        if (keycloak.token) {
                            sessionStorage.setItem("kc-token", keycloak.token);
                        }
                        if (keycloak.refreshToken) {
                            sessionStorage.setItem("kc-refresh-token", keycloak.refreshToken);
                        }
                        // Update organization cache from refreshed token
                        saveOrganizationFromToken();
                    }
                }
                catch (error) {
                    logger.debug("Token refresh failed:", error);
                    authState.isAuthenticated = false;
                    sessionStorage.removeItem("kc-token");
                    sessionStorage.removeItem("kc-refresh-token");
                    clearSession();
                    if (window.location.pathname !== "/session-ended" &&
                        !window.location.pathname.startsWith("/error")) {
                        window.location.replace("/session-ended");
                    }
                }
            }, 60000);
        }
    }
    catch (error) {
        logger.debug("Keycloak initialization failed:", error);
        authState.isAuthenticated = false;
        authState.isInitialized = true;
    }
}
// On a customer hostname, swaps in the tenant's Keycloak client. CONSTRAINT: await it before
// app.use(router): a route guard can call login(), and keycloak-js reads its config in init().
export async function configureKeycloakForHost() {
    const host = await loadByoHost();
    if (!host) {
        return;
    }
    try {
        // loadByoHost() returns an entry in PROD builds only, where keycloakConfig is the file path.
        const response = await fetch(keycloakConfig);
        const base = (await response.json());
        keycloak = new Keycloak({
            url: base["auth-server-url"],
            realm: base.realm,
            clientId: host.clientId,
        });
        authState.keycloakInstance = markRaw(keycloak);
        byoHost = host;
    }
    catch (error) {
        logger.error("Failed to configure the Keycloak client for this hostname:", error);
    }
}
export default {
    install: (app) => {
        app.provide(KEYCLOAK_KEY, authState);
        const pathname = window.location.pathname;
        // Skip Keycloak initialization for login/logout pages
        if (pathname === "/session-ended" || pathname.includes("/session-ended")) {
            logger.debug(`Skipping Keycloak initialization for login page: ${pathname}`);
            authState.isInitialized = true;
            authState.isAuthenticated = false;
            return;
        }
        if (isPublicPage(pathname)) {
            logger.debug(`Skipping Keycloak initialization for public page: ${pathname}`);
            authState.isInitialized = true;
            authState.isAuthenticated = false;
            return;
        }
        initializeKeycloak().catch((error) => {
            logger.debug("Failed to initialize Keycloak:", error);
        });
    },
};
export function useKeycloak() {
    const auth = inject(KEYCLOAK_KEY);
    if (!auth) {
        throw new Error("Keycloak instance not found. Make sure the plugin is registered in main.ts.");
    }
    return auth;
}
export { authState, keycloak };
